import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/transferRecieptAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchGrid = createAsyncThunk(
    'transferReciept/fetchGrid',
    async ({ roleId, uid }, { rejectWithValue }) => {
        try {
            const res = await api.getTransferRecieptDetails(roleId, uid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load the MRR queue'));
        }
    }
);

export const fetchItemDetails = createAsyncThunk(
    'transferReciept/fetchItemDetails',
    async ({ refno, type, potype }, { rejectWithValue }) => {
        try {
            const res = await api.getTransferRecieptDataGrid(refno, type, potype);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load item details'));
        }
    }
);

// "From Other Store" receipts. Success format is "Submitted,<MRRNo>".
export const submitOtherStoreReciept = createAsyncThunk(
    'transferReciept/submitOtherStore',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveTransferReciept(payload);
            const raw = res?.Data || '';
            const [status, mrrNo] = raw.split(',');
            if (status !== 'Submitted') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return { mrrNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// "From Vendor" (PO) receipts. Success format is "Submitted,<MRRNo>,<DownloadExt>" —
// DownloadExt doubles as the S3 upload key for the attached PDF.
export const submitVendorReciept = createAsyncThunk(
    'transferReciept/submitVendor',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveVendorTransferReciept(payload);
            const raw = res?.Data || '';
            const [status, mrrNo, downloadExt] = raw.split(',');
            if (status !== 'Submitted') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return { mrrNo, downloadExt };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    gridItems: [],
    itemDetails: [],
    submitResult: null,
    loading: {
        grid: false,
        itemDetails: false,
        submit: false,
    },
    errors: {
        grid: null,
        itemDetails: null,
        submit: null,
    },
};

const transferRecieptSlice = createSlice({
    name: 'transferReciept',
    initialState,
    reducers: {
        clearItemDetails:  (s) => { s.itemDetails = []; s.errors.itemDetails = null; },
        clearSubmitResult: (s) => { s.submitResult = null; s.errors.submit = null; },
        resetTransferReciept: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridItems = a.payload; })
            .addCase(fetchGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(fetchItemDetails.pending,   (s) => { s.loading.itemDetails = true; s.errors.itemDetails = null; })
            .addCase(fetchItemDetails.fulfilled, (s, a) => { s.loading.itemDetails = false; s.itemDetails = a.payload; })
            .addCase(fetchItemDetails.rejected,  (s, a) => { s.loading.itemDetails = false; s.errors.itemDetails = a.payload; s.itemDetails = []; });

        builder
            .addCase(submitOtherStoreReciept.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitOtherStoreReciept.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; })
            .addCase(submitOtherStoreReciept.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });

        builder
            .addCase(submitVendorReciept.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitVendorReciept.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; })
            .addCase(submitVendorReciept.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearItemDetails,
    clearSubmitResult,
    resetTransferReciept,
} = transferRecieptSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectTRGridItems    = (s) => s.transferReciept.gridItems;
export const selectTRItemDetails  = (s) => s.transferReciept.itemDetails;
export const selectTRSubmitResult = (s) => s.transferReciept.submitResult;
export const selectTRLoading      = (s) => s.transferReciept.loading;
export const selectTRErrors       = (s) => s.transferReciept.errors;

export default transferRecieptSlice.reducer;
